import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, ChevronDown, ChevronRight, CheckCircle2, Sparkles, ShieldCheck, Layers } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'sip_token';

const DEFAULT_TRIO = new Set(['lesson_plan', 'worksheet', 'class_test']);  // "Insta-Kit"

export default function BoscoCaptureKitPicker() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem(TOKEN_KEY) || '';
        const r = await fetch(`${API_URL}/api/bosco-capture/kit/catalog`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`Catalog fetch failed (${r.status})`);
        const d = await r.json();
        setCatalog(d.catalog || []);
        // Expand the first two categories by default so the picker doesn't look empty.
        setExpanded({ [d.catalog?.[0]?.category]: true, [d.catalog?.[1]?.category]: true });
      } catch (e) {
        setError(e.message);
      }
    })();
  }, []);

  const totalSelected = selected.size;

  const toggleItem = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const applyInstaKit = () => setSelected(new Set(DEFAULT_TRIO));

  const submit = async () => {
    if (!totalSelected) { toast.error('Pick at least one resource.'); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY) || '';
      const r = await fetch(`${API_URL}/api/bosco-capture/session/${sessionId}/generate-kit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ items: Array.from(selected) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail?.message || `Kit generation failed (${r.status})`);
      toast.success(`${d.jobs.length} resource${d.jobs.length === 1 ? '' : 's'} queued.`);
      // Hand the fan-out plan straight to the progress screen so we don't
      // have to re-fetch it (avoids a race with the just-created kit doc).
      navigate(`/bosco-capture/kit/progress/${d.kitId}`, { state: { plan: d } });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-100 bg-slate-950 p-6" data-testid="bosco-capture-kit-error">
        <div className="text-center max-w-sm">
          <p className="text-sm text-rose-300">{error}</p>
          <Button className="mt-4" onClick={() => navigate('/teacher-assignments')}>Back</Button>
        </div>
      </div>
    );
  }
  if (!catalog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100" data-testid="bosco-capture-kit-loading">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-950 to-emerald-950/40 text-slate-100 pb-32"
         data-testid="bosco-capture-kit-picker">
      <div className="max-w-md mx-auto px-5 pt-6">
        <div className="flex items-center gap-2 text-emerald-300 text-[10px] uppercase tracking-[0.2em] font-semibold">
          <Layers className="h-3 w-3" /> Teaching Kit
        </div>
        <h1 className="text-2xl font-bold mt-1">What should BOSCO generate?</h1>
        <p className="text-xs text-slate-400 mt-1">
          Pick as many resources as you like. Only your selections will be produced
          — running through the same Content Engine you already use.
        </p>

        <button
          type="button"
          onClick={applyInstaKit}
          className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-sm font-semibold hover:bg-emerald-500/25 transition-colors"
          data-testid="bosco-capture-kit-instakit"
        >
          <Sparkles className="h-4 w-4" /> Insta-Kit · Lesson plan + Worksheet + Class test
        </button>

        <div className="mt-5 space-y-2">
          {catalog.map((cat) => {
            const isOpen = !!expanded[cat.category];
            const catSelectedCount = cat.items.filter((it) => selected.has(it.id)).length;
            return (
              <div key={cat.category} className="rounded-lg border border-white/10 bg-white/5 overflow-hidden"
                   data-testid={`bosco-capture-kit-cat-${slug(cat.category)}`}>
                <button
                  type="button"
                  onClick={() => setExpanded((s) => ({ ...s, [cat.category]: !s[cat.category] }))}
                  className="w-full flex items-center justify-between p-3 text-sm font-semibold text-left hover:bg-white/5"
                >
                  <span className="flex items-center gap-2">
                    {isOpen ? <ChevronDown className="h-4 w-4 text-emerald-400" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                    {cat.category}
                  </span>
                  {catSelectedCount > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 font-mono">
                      {catSelectedCount}
                    </span>
                  )}
                </button>
                {isOpen && (
                  <ul className="divide-y divide-white/5">
                    {cat.items.map((it) => {
                      const on = selected.has(it.id);
                      return (
                        <li key={it.id}>
                          <label className="flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 cursor-pointer text-sm">
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleItem(it.id)}
                              className="h-4 w-4 accent-emerald-500"
                              data-testid={`bosco-capture-kit-item-${it.id}`}
                            />
                            <span className={on ? 'text-emerald-200' : 'text-slate-300'}>{it.label}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>

        <p className="mt-5 text-[10px] text-slate-500 text-center flex items-center justify-center gap-1">
          <ShieldCheck className="h-3 w-3" />
          Captured media already deleted — generation runs on your saved understanding only.
        </p>
      </div>

      {/* Sticky footer */}
      <div className="fixed inset-x-0 bottom-0 z-40 bg-black/95 border-t border-white/10 backdrop-blur px-4 py-4"
           style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
        <div className="max-w-md mx-auto flex items-center gap-3">
          <div className="text-xs text-slate-400 flex-1">
            <span className="text-emerald-300 font-semibold text-base">{totalSelected}</span> selected
          </div>
          <Button
            variant="outline"
            className="bg-transparent border-white/20 text-slate-200"
            onClick={() => navigate('/teacher-assignments')}
            disabled={submitting}
            data-testid="bosco-capture-kit-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting || totalSelected === 0}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="bosco-capture-kit-generate"
          >
            {submitting
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <><CheckCircle2 className="h-4 w-4 mr-1" /> Generate</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function slug(s) { return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
